// A tapped widget or Live Activity, from a cold start or from appUrlOpen.
// Moved out of main.js so the order it depends on can be tested.
//
// `ready` is the boot load of the city's network (`bootNetwork`): every station
// lookup below is synchronous over that bundle, and a cold start delivers the
// link BEFORE the chunk has arrived, when a lookup answers "no such station"
// and the tap is dropped.

import { parseWidgetLink } from './widget-link.js';
import { requestBoard } from './board-request.js';
import { runPath } from './run-redirect.js';

export async function openRoute(url, { router, ready = Promise.resolve() }) {

  const link = parseWidgetLink(url);
  if (!link) return;
  await ready;

  // `network.js` is imported here rather than at the top of the file because
  // it drags a city's whole station bundle in with it, and that belongs to the
  // departures chunk. A cold start that is not a deep link never loads it.
  const [{ city, switchCity }, { stationById }] = await Promise.all([
    import('./app-state.js'),
    import('./network.js'),
  ]);

  // The city first, or the stop id is read against the wrong bundle — which
  // does not fail, it quietly matches nothing.
  if (link.city && link.city !== city.value) await switchCity(link.city);

  // ── A TRAIN, where the link names one ──────────────────────────────────
  //
  // Tapping a Live Activity has to open the train the card is about. It used
  // to open the BOARD at the station the train departs from, because that is
  // all the link ever said; now the link names the run and this opens it.
  //
  // STRAIGHT TO THE PAGE, WITHOUT RESOLVING THE RUN FIRST. The temptation is
  // to look the train up and fall back to the board when it cannot be found,
  // and that is the silent-fallback fault in a new place: somebody who taps a
  // card about a train they are sitting on must not be put somewhere else with
  // no explanation. The train page already knows how to say it cannot read a
  // run, and it is the page that should say it.
  if (link.runRef) {
    const stop = link.stop || 0;
    const route = link.routeId ? `?route=${link.routeId}` : '';
    // A tram's (or any mode's) ride opens its own run screen; the train path
    // stays for trains, and the router redirects a mode's ref sent down it.
    const own = runPath({ runRef: link.runRef, stop, routeId: link.routeId, mode: link.mode });
    router
      .push(own ?? `/tabs/train/${encodeURIComponent(link.runRef)}/${stop}${route}`)
      .catch((e) => {
        console.warn(`route: could not open train ${link.runRef} (${e?.message ?? 'no route'})`);
      });
    return;
  }

  const station = stationById(link.stop);
  if (!station) {
    // NOT SILENT. A link that names a station this bundle does not have is a
    // city mismatch or a stale widget, and dropping it left somebody on
    // whatever screen they happened to be on with no idea why.
    console.warn(`route: no station ${link.stop} in ${city.value}`);
    return;
  }
  requestBoard(station);
  router.push('/tabs/departures').catch(() => { /* already on it — the board watches */ });
}
